import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import PageForm from '../../templates/PageForm';
import { recommendationBFFService } from '../../../services/RecommendationBFFService';

const RecommendationEngineDocumentDetails: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { batchId } = useParams<{ batchId: string }>();
    const document = location.state?.document;
    const recommendation = location.state?.recommendation;
    const cachedBatchData = location.state?.batchData;

    const API_KEY = process.env.REACT_APP_API_KEY || '';
    const [batchData, setBatchData] = useState<any>(cachedBatchData || null);
    const [documentData, setDocumentData] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (batchId && document) {
            loadDocumentDetails();
        }
    }, [batchId, document]);

    const loadDocumentDetails = async () => {
        try {
            setLoading(true);

            // ✅ OTIMIZAÇÃO: Usar dados do cache se disponíveis
            let enrichedBatchData = cachedBatchData;
            
            if (!enrichedBatchData) {
                // Fallback: buscar via BFF apenas se não tiver cache
                console.log(`🔄 Cache não disponível, carregando lote ${batchId} via BFF`);
                enrichedBatchData = await recommendationBFFService.analyzeBatch(batchId!);
            } else {
                console.log(`✅ Usando dados do cache para lote ${batchId}`);
            }

            console.log('📦 Batch data:', enrichedBatchData);
            console.log('🔍 Procurando documento:', document);

            setBatchData(enrichedBatchData);

            // Encontrar todos os registros do documento específico
            const decodedDocument = decodeURIComponent(document || '');
            const foundDocuments = enrichedBatchData.reports?.filter((report: any) => report.document === decodedDocument) || [];

            console.log('✅ Documentos encontrados:', foundDocuments.length);

            if (foundDocuments.length > 0) {
                setDocumentData(foundDocuments);
                console.log('✅ setDocumentData chamado com:', foundDocuments);
            } else {
                setError(`Documento ${decodedDocument} não encontrado no lote`);
            }

        } catch (error) {
            console.error('Erro ao carregar detalhes do documento:', error);
            setError(error instanceof Error ? error.message : 'Erro desconhecido');
        } finally {
            setLoading(false);
        }
    };

    const getRecommendationBadge = (recommendation: string | null) => {
        if (!recommendation) {
            return <span className="px-5 py-4 rounded-full text-sm font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">Não informado</span>;
        }

        const badges: Record<string, { className: string; label: string }> = {
            'PENDING': { className: 'px-5 py-4 rounded-full text-base font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Pendente' },
            'PENDENTE': { className: 'px-5 py-4 rounded-full text-base font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Pendente' },
            'APPROVED': { className: 'px-5 py-4 rounded-full text-base font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Aprovado' },
            'APROVADO': { className: 'px-5 py-4 rounded-full text-base font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Aprovado' },
            'REJECTED': { className: 'px-5 py-4 rounded-full text-base font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Reprovado' },
            'REPROVADO': { className: 'px-5 py-4 rounded-full text-base font-semibold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Reprovado' }
        };

        const badge = badges[recommendation.toUpperCase()] || badges['APPROVED'];
        return <span className={badge.className}>{badge.label}</span>;
    };

    const renderFieldValue = (value: any, fieldType?: string) => {
        if (value === null || value === undefined || value === '') {
            return <span className="text-gray-400 italic">Não informado</span>;
        }

        if (typeof value === 'boolean') {
            return (
                <span className={`px-2 py-1 rounded text-xs font-medium ${value ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                    {value ? 'Sim' : 'Não'}
                </span>
            );
        }

        if (typeof value === 'object' && !Array.isArray(value)) {
            return <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">{JSON.stringify(value, null, 2)}</pre>;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <span className="text-gray-400 italic">Vazio</span>;
            }

            // Se são objetos, renderizar como cards
            if (typeof value[0] === 'object') {
                return (
                    <div className="space-y-2 max-h-96 overflow-auto">
                        {value.map((item, index) => (
                            <div key={index} className="bg-gray-100 dark:bg-slate-500 p-3 rounded text-sm border-l-4 border-blue-500">
                                <div className="font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                    Item {index + 1}
                                </div>
                                {Object.entries(item).map(([key, val]) => (
                                    <div key={key} className="mb-1">
                                        <span className="font-medium text-gray-600 dark:text-gray-300">
                                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                                        </span>{' '}
                                        <span className="text-gray-900 dark:text-gray-100">
                                            {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                );
            }

            // Se são valores simples, mostrar como lista
            return (
                <ul className="list-disc list-inside text-sm">
                    {value.map((item, index) => (
                        <li key={index}>{String(item)}</li>
                    ))}
                </ul>
            );
        }

        return <span>{String(value)}</span>;
    };

    const renderDynamicFields = (data: any) => {
        if (!data) {
            console.warn('⚠️ Sem data');
            return <div className="text-gray-500">Sem dados disponíveis</div>;
        }

        // Campos que não devem ser exibidos (metadados e campos backend que serão mostrados separadamente)
        const excludeFields = ['document', 'createdAt', 'updatedAt', 'status', 'statusFormatted',
            'reportType', 'reportTypeId', 'batchReportId', 'attempts', 'maxAttempts', 'events',
            'backend_recommendation', 'backend_reasons', 'backend_risk_score',
            'backend_analysis_id', 'backend_document_type', 'analysis_status', 'name'];

        const entries = Object.entries(data).filter(([key]) => !excludeFields.includes(key));

        if (entries.length === 0) {
            return <div className="text-gray-500">Payload vazio</div>;
        }

        const providerName = batchData?.provider || 'Provider';

        return (
            <div className="space-y-8">
                {/* Seção de Análise Backend */}
                {data.backend_recommendation && (
                    <div className="mb-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Coluna 1: Informações do Documento */}
                            <div className="bg-gray-50 dark:bg-slate-600 p-3 rounded">
                                <div className="space-y-3">
                                    <div>
                                        <div className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                                            Documento
                                        </div>
                                        <div className="text-gray-900 dark:text-gray-100">
                                            {data.document}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                                            Nome/Razão Social
                                        </div>
                                        <div className="text-gray-900 dark:text-gray-100">
                                            {data.name || 'Não informado'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Coluna 2: Recomendação */}
                            <div className="bg-gray-50 dark:bg-slate-600 p-3 rounded">
                                <div className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-7">
                                    Recomendação
                                </div>
                                <div className="text-gray-900 dark:text-gray-100">
                                    {getRecommendationBadge(data.backend_recommendation)}
                                </div>
                            </div>

                            {/* Resumo de Regras - Linha completa */}
                            {data.backend_reasons && Array.isArray(data.backend_reasons) && data.backend_reasons.length > 0 && (
                                <div className="bg-gray-50 dark:bg-slate-600 p-3 rounded md:col-span-2">
                                    <div className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                                        Resumo de Regras Acionadas
                                    </div>
                                    <div className="text-gray-900 dark:text-gray-100">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                            {data.backend_reasons.map((reason: string, idx: number) => (
                                                <div key={idx} className="flex items-start gap-2">
                                                    <span className="text-blue-600 dark:text-blue-400">•</span>
                                                    <span className="text-sm">{reason}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Seção de Dados do Provider */}
                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                        Dados do {providerName}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {entries.map(([key, value], index) => {
                            // Verificar se é um campo simples (número, string curta, boolean)
                            const isSimpleField = typeof value === 'number' ||
                                typeof value === 'boolean' ||
                                (typeof value === 'string' && value.length < 50);

                            // Verificar se é um array ou objeto complexo
                            const isComplexField = Array.isArray(value) ||
                                (typeof value === 'object' && value !== null);

                            return (
                                <div
                                    key={index}
                                    className={`bg-gray-50 dark:bg-slate-600 p-3 rounded ${isComplexField ? 'md:col-span-2' : ''
                                        }`}
                                >
                                    <div className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </div>
                                    <div className="text-gray-900 dark:text-gray-100">
                                        {renderFieldValue(value)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const renderInovamindFields = (data: any) => {
        const sections = [
            {
                title: 'Informações Básicas',
                fields: [
                    { key: 'name', label: 'Nome' },
                    { key: 'document', label: 'Documento' },
                    { key: 'createdAt', label: 'Data de Criação' },
                    { key: 'statusFormatted', label: 'Status' }
                ]
            },
            {
                title: 'Análise Backend',
                fields: [
                    { key: 'backend_recommendation', label: 'Recomendação', isRecommendation: true },
                    { key: 'backend_reasons', label: 'Resumo de Regras Acionadas', isArray: true }
                ]
            },
            {
                title: 'PEP (Pessoa Exposta Politicamente)',
                fields: [
                    { key: 'pep_search_v2', label: 'PEP' },
                    { key: 'pep_reason', label: 'Motivo PEP' }
                ]
            },
            {
                title: 'Órgãos Reguladores',
                fields: [
                    { key: 'bacen_qgi_cpf', label: 'BACEN QGI (Inabilitados)' },
                    { key: 'cvm_sanctioning_administrative_process', label: 'CVM (Processo Administrativo)' },
                    { key: 'ceaf_infos_government_base', label: 'CEAF (Cadastro de Expulsões)' },
                    { key: 'ceis_infos_government_base_cpf', label: 'CEIS (Empresas Inidôneas)' },
                    { key: 'administrative_improbity_cpf', label: 'CNJ (Improbidade Administrativa)' }
                ]
            },
            {
                title: 'Sanções Internacionais',
                fields: [
                    { key: 'sanctions_list', label: 'OFAC (Lista de Sanções)' },
                    { key: 'eu_sanction_list', label: 'Sanções União Europeia' },
                    { key: 'fr_sanction_list', label: 'Sanções França' },
                    { key: 'uk_sanction_list', label: 'Sanções Reino Unido' },
                    { key: 'onu_sanction_lists', label: 'Sanções ONU' },
                    { key: 'us_nonproliferation_sanction_list', label: 'Sanções EUA (Não Proliferação)' }
                ]
            },
            {
                title: 'Segurança e Justiça',
                fields: [
                    { key: 'red_notice_interpol', label: 'Red Notice Interpol' },
                    { key: 'court_warrant_bnmp_cpf', label: 'Mandado de Prisão (BNMP)' },
                    { key: 'court_warrant_bnmp', label: 'Detalhes BNMP' },
                    { key: 'criminal_record_federal_police', label: 'Antecedentes Criminais (PF)' }
                ]
            },
            {
                title: 'Meio Ambiente e Trabalho',
                fields: [
                    { key: 'work_slave_cpf', label: 'Trabalho Escravo' },
                    { key: 'ibama_negative_certificate_cpf', label: 'IBAMA - Certidão Negativa' },
                    { key: 'ibama_assessment_cpf', label: 'IBAMA - Multas' },
                    { key: 'ibama_embargo_cpf', label: 'IBAMA - Embargos' }
                ]
            }
        ];

        return sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                    {section.title}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((field: any, fieldIndex: number) => (
                        <div key={fieldIndex} className="bg-gray-50 dark:bg-slate-600 p-3 rounded">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {field.label}
                            </div>
                            <div className="text-gray-900 dark:text-gray-100">
                                {field.isRecommendation
                                    ? getRecommendationBadge(data[field.key])
                                    : renderFieldValue(data[field.key])
                                }
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ));
    };

    const renderEscavadorFields = (data: any) => {
        const sections = [
            {
                title: 'Informações Básicas',
                fields: [
                    { key: 'nome', label: 'Nome' },
                    { key: 'document', label: 'Documento' },
                    { key: 'createdAt', label: 'Data de Criação' },
                    { key: 'statusFormatted', label: 'Status' }
                ]
            },
            {
                title: 'Processo Judicial',
                fields: [
                    { key: 'numero_processo', label: 'Número do Processo' },
                    { key: 'classe', label: 'Classe' },
                    { key: 'assunto', label: 'Assunto' },
                    { key: 'sigla_assunto', label: 'Sigla do Assunto' },
                    { key: 'tribunal', label: 'Tribunal' },
                    { key: 'situacao', label: 'Situação' }
                ]
            },
            {
                title: 'Partes do Processo',
                fields: [
                    { key: 'titulo_polo_ativo', label: 'Polo Ativo' },
                    { key: 'titulo_polo_passivo', label: 'Polo Passivo' }
                ]
            },
            {
                title: 'Datas e Valores',
                fields: [
                    { key: 'data_distribuicao', label: 'Data de Distribuição' },
                    { key: 'data_ultima_movimentacao', label: 'Última Movimentação' },
                    { key: 'data_ultima_verificacao', label: 'Última Verificação' },
                    { key: 'valor_causa_valor', label: 'Valor da Causa' }
                ]
            },
            {
                title: 'Informações Adicionais',
                fields: [
                    { key: 'arquivado', label: 'Arquivado' },
                    { key: 'segredo_justica', label: 'Segredo de Justiça' }
                ]
            }
        ];

        return sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                    {section.title}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields.map((field, fieldIndex) => (
                        <div key={fieldIndex} className="bg-gray-50 dark:bg-slate-600 p-3 rounded">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {field.label}
                            </div>
                            <div className="text-gray-900 dark:text-gray-100">
                                {renderFieldValue(data[field.key])}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ));
    };


    if (loading) {
        return (
            <PageForm
                pageTitle="Carregando..."
                apiVersion="Recommendation Engine - 1.0.0"
                isLoading={true}
                breadcrumb={[
                    { label: 'Recomendações', url: '/recommendation-engine/recommendations/0/20/createdAt/DESC' },
                    { label: 'Detalhes do Lote', url: `/recommendation-engine/recommendations/${batchId}` },
                    { label: 'Detalhes do Documento', url: '#' }
                ]}
                controlSlot={<div></div>}
            >
                <div className="text-center">
                    <div className="text-gray-600 dark:text-gray-400">
                        Carregando detalhes do documento...
                    </div>
                </div>
            </PageForm>
        );
    }

    if (error) {
        return (
            <PageForm
                pageTitle="Erro"
                apiVersion="Recommendation Engine - 1.0.0"
                isLoading={false}
                breadcrumb={[
                    { label: 'Recomendações', url: '/recommendation-engine/recommendations/0/20/createdAt/DESC' },
                    { label: 'Detalhes do Lote', url: `/recommendation-engine/recommendations/${batchId}` },
                    { label: 'Detalhes do Documento', url: '#' }
                ]}
                controlSlot={<div></div>}
            >
                <div className="text-center">
                    <div className="text-red-600 dark:text-red-400">
                        {error}
                    </div>
                    <button
                        onClick={() => navigate(`/recommendation-engine/recommendations/${batchId}`)}
                        className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                        ← Voltar para o Lote
                    </button>
                </div>
            </PageForm>
        );
    }

    console.log('🎨 Renderizando com documentData:', documentData, 'length:', documentData.length);

    return (
        <PageForm
            pageTitle={`Detalhes: ${documentData[currentPage]?.name || documentData[currentPage]?.nome || documentData[currentPage]?.document} (${currentPage + 1}/${documentData.length})`}
            apiVersion="Recommendation Engine - 1.0.0"
            isLoading={false}
            breadcrumb={[
                { label: 'Recomendações', url: '/recommendation-engine/recommendations/0/20/createdAt/DESC' },
                { label: 'Detalhes do Lote', url: `/recommendation-engine/recommendations/${batchId}` },
                { label: 'Detalhes do Documento', url: '#' }
            ]}
            controlSlot={<div></div>}
        >
            {/* Barra de controles fixa */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-600 p-4 mb-6">
                <div className="flex justify-between items-center">
                    <button
                        onClick={() => navigate(`/recommendation-engine/recommendations/${batchId}`)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                    >
                        <IoMdArrowRoundBack />
                        Voltar para o Lote
                    </button>

                    <div className="flex items-center gap-4">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Provedor: <span className="font-medium">{batchData?.provider}</span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Documento: <span className="font-medium">{documentData[currentPage]?.document}</span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Total de registros: <span className="font-medium">{documentData.length}</span>
                        </div>

                        {documentData.length > 1 && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                                    disabled={currentPage === 0}
                                    className="px-3 py-1 bg-gray-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    ← Anterior
                                </button>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {currentPage + 1} de {documentData.length}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(Math.min(documentData.length - 1, currentPage + 1))}
                                    disabled={currentPage === documentData.length - 1}
                                    className="px-3 py-1 bg-gray-600 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    Próximo →
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow p-6">
                {documentData.length > 0 && (
                    <>
                        {renderDynamicFields(documentData[currentPage])}
                    </>
                )}
            </div>
        </PageForm>
    );
};

export default RecommendationEngineDocumentDetails;
